import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { PowerLevelBadge, PowerLevelInline } from '../components/PowerLevelBadge';
import { TrophyCase, StreakFire } from '../components/TrophyCase';
import { playersAPI, matchesAPI } from '../lib/api';
import { getInitials, getSkillLevelColor, formatDate, formatDuration } from '../lib/utils';
import { getPowerTier } from '../lib/powerLevel';
import { TrendingUp, Award, Target, Users, Activity, Zap, Clock } from 'lucide-react';

export default function PlayerProfile() {
  const { userId } = useParams();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Player profile is the required call. Matches + players-lookup are
        // best-effort so an anonymous viewer still sees the profile when one
        // of the secondary lists is empty or briefly errors out.
        const playerRes = await playersAPI.getById(userId);
        setPlayer(playerRes.data);

        try {
          const matchesRes = await matchesAPI.getAll();
          const playerMatches = (matchesRes.data || []).filter(m =>
            m.player1_id === userId || m.player2_id === userId || m.player1_partner_id === userId || m.player2_partner_id === userId
          );
          setMatches(playerMatches);
        } catch { /* leave matches empty */ }

        try {
          const allPlayersRes = await playersAPI.getAll();
          const lookup = {};
          (allPlayersRes.data || []).forEach(p => { lookup[p.user_id] = p; });
          setPlayers(lookup);
        } catch { /* leave lookup empty — IDs will fall back to "Unknown" */ }
      } catch (error) {
        console.error('Error fetching player:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const getPlayerName = (id) => players[id]?.name || 'Unknown';

  if (loading) return <Layout><div className="space-y-6"><Skeleton className="h-32 w-full" /><div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-64 w-full" /></div></Layout>;
  if (!player) return <Layout><Card><CardContent className="py-12 text-center"><Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-40" /><p className="text-muted-foreground">{t('players.notFound')}</p></CardContent></Card></Layout>;

  const stats = player.stats || {};
  const elo = stats.elo_rating || 1200;
  const winRate = stats.matches_played ? Math.round((stats.matches_won / stats.matches_played) * 100) : 0;
  const tier = getPowerTier(elo);

  return (
    <Layout>
      <div className="space-y-6" data-testid="player-profile-page">
        {/* Profile Header */}
        <Card className="overflow-hidden">
          <div className="h-2" style={{ background: `linear-gradient(90deg, ${tier.color}80, ${tier.color}20)` }} />
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              <Avatar className="h-20 w-20 ring-2" style={{ '--tw-ring-color': tier.color }}>
                <AvatarImage src={player.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">{getInitials(player.name)}</AvatarFallback>
              </Avatar>
              <div className="text-center md:text-left flex-1">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold">{player.name}</h1>
                <p className="text-sm text-muted-foreground">{player.email}</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-2">
                  <Badge className={`${getSkillLevelColor(player.skill_level)} capitalize text-xs`}>{player.skill_level}</Badge>
                  {stats.current_streak >= 3 && (
                    <Badge variant="outline" className="flex items-center gap-1 text-xs">
                      <StreakFire currentStreak={stats.current_streak} />
                      {stats.current_streak} streak
                    </Badge>
                  )}
                </div>
              </div>
              <PowerLevelBadge elo={elo} size="lg" />
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t('dashboard.matchesPlayed'), value: stats.matches_played || 0, icon: Activity, color: 'text-primary' },
            { label: t('dashboard.winRate'), value: `${winRate}%`, icon: TrendingUp, color: 'text-secondary' },
            { label: t('dashboard.bestStreak'), value: <span className="flex items-center gap-1">{stats.best_streak || 0}<StreakFire currentStreak={stats.best_streak || 0} /></span>, icon: Zap, color: 'text-accent' },
            { label: t('players.pointsScored'), value: stats.total_points_scored || 0, icon: Target, color: 'text-purple-500' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label}>
                <CardContent className="p-4 text-center">
                  <Icon className={`h-6 w-6 mx-auto mb-1.5 ${stat.color}`} />
                  <p className="text-xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trophy Case + Rating Breakdown */}
        <div className="grid md:grid-cols-2 gap-6">
          <TrophyCase stats={stats} />

          <Card>
            <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base">{t('players.ratingBreakdown')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center"><span className="text-sm">{t('players.singlesRating')}</span><PowerLevelInline elo={stats.singles_rating || 1200} /></div>
              <div className="flex justify-between items-center"><span className="text-sm">{t('players.doublesRating')}</span><PowerLevelInline elo={stats.doubles_rating || 1200} /></div>
              <div className="flex justify-between items-center"><span className="text-sm">{t('players.overallElo')}</span><PowerLevelInline elo={elo} /></div>
              <hr className="border-border" />
              <div className="flex justify-between items-center"><span className="text-sm">{t('players.wins')}</span><span className="font-bold text-secondary">{stats.matches_won || 0}</span></div>
              <div className="flex justify-between items-center"><span className="text-sm">{t('players.losses')}</span><span className="font-bold text-destructive">{stats.matches_lost || 0}</span></div>
              <div className="flex justify-between items-center"><span className="text-sm">{t('players.currentStreak')}</span><span className="font-bold flex items-center gap-1">{stats.current_streak || 0}<StreakFire currentStreak={stats.current_streak || 0} /></span></div>
            </CardContent>
          </Card>
        </div>

        {/* Head-to-Head Records */}
        <Card>
          <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base flex items-center gap-2"><Target className="h-4 w-4 text-primary" />Head-to-Head Records</CardTitle></CardHeader>
          <CardContent>
            {(() => {
              const h2h = {};
              matches.filter(m => m.status === 'completed').forEach(m => {
                const isP1 = m.player1_id === userId;
                const oppId = isP1 ? m.player2_id : m.player1_id;
                if (!h2h[oppId]) h2h[oppId] = { wins: 0, losses: 0, last: null };
                if (m.winner_id === userId) h2h[oppId].wins++;
                else h2h[oppId].losses++;
                if (!h2h[oppId].last || m.created_at > h2h[oppId].last) h2h[oppId].last = m.created_at;
              });
              const entries = Object.entries(h2h).sort((a, b) => (b[1].wins + b[1].losses) - (a[1].wins + a[1].losses));
              if (entries.length === 0) return <p className="text-muted-foreground text-sm text-center py-4">No head-to-head records yet</p>;
              return (
                <div className="space-y-2">
                  {entries.slice(0, 10).map(([oppId, record]) => (
                    <Link key={oppId} to={`/players/${oppId}`}>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8"><AvatarFallback className="text-xs">{getInitials(getPlayerName(oppId))}</AvatarFallback></Avatar>
                          <div>
                            <p className="font-medium text-sm">{getPlayerName(oppId)}</p>
                            <p className="text-xs text-muted-foreground">Last: {record.last ? formatDate(record.last) : '-'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm"><span className="text-green-500 font-bold">{record.wins}</span> - <span className="text-red-400 font-bold">{record.losses}</span></span>
                          <Badge variant="outline" className="text-xs">{record.wins + record.losses} played</Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Recent Matches */}
        <Card>
          <CardHeader><CardTitle className="font-heading uppercase tracking-tight text-base">{t('players.recentMatches')}</CardTitle></CardHeader>
          <CardContent>
            {matches.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><Activity className="h-10 w-10 mx-auto mb-3 opacity-40" /><p className="text-sm">{t('players.noMatchesPlayed')}</p></div>
            ) : (
              <div className="space-y-2">
                {matches.slice(0, 10).map(match => {
                  const isP1 = match.player1_id === userId;
                  const won = match.winner_id === userId;
                  const opponent = isP1 ? match.player2_id : match.player1_id;
                  return (
                    <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <Badge className={`${won ? 'bg-secondary' : 'bg-destructive'} text-xs`}>{won ? 'W' : 'L'}</Badge>
                          <div>
                            <p className="font-medium text-sm">vs {getPlayerName(opponent)}</p>
                            <p className="text-xs text-muted-foreground">{match.match_type} - {formatDate(match.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-sm">{match.games?.map(g => `${g.player1_score}-${g.player2_score}`).join(', ')}</p>
                          {match.duration_seconds > 0 && <p className="text-xs text-muted-foreground flex items-center justify-end gap-1"><Clock className="h-3 w-3" />{formatDuration(match.duration_seconds)}</p>}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
